/**
 * Гейт возможностей устройства для «Объём 3D» (запрос владельца 27.07:
 * «самое плохое — красиво, но тормозит»). Решение принимается ДО загрузки
 * three.js (~170 КБ gzip): на слабом устройстве тяжёлый чанк даже не качается,
 * пользователю остаётся лёгкий плоский «Объём» на MapLibre.
 *
 * Три уровня:
 *  - 'off'  — 3D не предлагаем вовсе (нет WebGL, программный рендер, экономия
 *             трафика, совсем мало памяти/ядер). Кнопки в карточке НЕТ.
 *  - 'lite' — 3D есть, но дешёвое: без физического стекла (transmission —
 *             самый дорогой материал), pixelRatio 1, без спрайтов-меток.
 *  - 'full' — полное качество.
 *
 * Проверки — только дешёвые синхронные (никаких бенчмарков на старте).
 */

export type VolumeTier = 'off' | 'lite' | 'full'

interface DeviceHints {
  readonly cores: number
  readonly memoryGb: number
  readonly saveData: boolean
  readonly slowNetwork: boolean
  readonly webgl: 'none' | 'software' | 'hardware'
  readonly coarsePointer: boolean
  readonly reducedMotion: boolean
}

/** Отдельная функция — чтобы решение было чистым и тестируемым. */
export function tierFromHints(h: DeviceHints): VolumeTier {
  // Без WebGL или на программном рендере (SwiftShader/llvmpipe) 3D — это слайд-шоу.
  if (h.webgl === 'none' || h.webgl === 'software') return 'off'
  // Экономия трафика/медленная сеть: не тянем тяжёлый чанк без спроса.
  if (h.saveData || h.slowNetwork) return 'off'
  // Совсем слабое железо: 2 ядра или ≤1 ГБ памяти.
  if (h.cores <= 2 || h.memoryGb <= 1) return 'off'
  // Средний класс (типичный бюджетный телефон) — упрощённый рендер.
  if (h.cores <= 4 || h.memoryGb <= 4) return 'lite'
  // Пользователь просил меньше движения — тоже облегчаем (без лишних эффектов).
  if (h.reducedMotion) return 'lite'
  return 'full'
}

/** WebGL: есть ли и не программный ли рендер (эвристика по строке рендера). */
function detectWebgl(): DeviceHints['webgl'] {
  try {
    const canvas = document.createElement('canvas')
    const gl = (canvas.getContext('webgl2') ??
      canvas.getContext('webgl')) as WebGLRenderingContext | null
    if (!gl) return 'none'
    const dbg = gl.getExtension('WEBGL_debug_renderer_info')
    const renderer = dbg
      ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL ?? 0) ?? '')
      : String(gl.getParameter(gl.RENDERER) ?? '')
    const soft = /swiftshader|llvmpipe|software|basic render|microsoft basic/i.test(renderer)
    // Контекст сразу отпускаем: он не нужен до открытия сцены.
    gl.getExtension('WEBGL_lose_context')?.loseContext()
    return soft ? 'software' : 'hardware'
  } catch {
    return 'none'
  }
}

function readHints(): DeviceHints {
  const nav = navigator as Navigator & {
    deviceMemory?: number
    connection?: { saveData?: boolean; effectiveType?: string }
  }
  const conn = nav.connection
  return {
    cores: nav.hardwareConcurrency ?? 4,
    // deviceMemory есть не везде; отсутствие — считаем средним классом.
    memoryGb: nav.deviceMemory ?? 4,
    saveData: conn?.saveData === true,
    slowNetwork: conn?.effectiveType === '2g' || conn?.effectiveType === 'slow-2g',
    webgl: detectWebgl(),
    coarsePointer: matchMedia?.('(pointer: coarse)').matches ?? false,
    reducedMotion: matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
  }
}

let cached: VolumeTier | null = null

/** Уровень 3D для этого устройства (считается один раз за сессию). */
export function volumeTier(): VolumeTier {
  if (cached === null) cached = tierFromHints(readHints())
  return cached
}

/** Ручной выбор владельца площадки/пользователя перекрывает автодетект. */
export function setVolumeTier(tier: VolumeTier): void {
  cached = tier
}

/** Настройки рендера под уровень — сцена читает их и не гадает сама. */
export interface TierSettings {
  /** Физическое стекло (transmission) — дорогой материал, только для full. */
  readonly glass: boolean
  /** Спрайты-метки арендаторов в сцене (иначе имена только в панели). */
  readonly labels: boolean
  readonly antialias: boolean
  readonly maxPixelRatio: number
  /** Рёбра контура: на слабых пропускаем — это отдельный draw call на этаж. */
  readonly edges: boolean
  /** Больше этажей не рисуем одновременно (высотки на слабом устройстве). */
  readonly maxFloors: number
}

export function settingsFor(tier: VolumeTier): TierSettings {
  if (tier === 'lite') {
    return {
      glass: false,
      labels: false,
      antialias: false,
      maxPixelRatio: 1,
      edges: false,
      maxFloors: 12,
    }
  }
  return {
    glass: true,
    labels: true,
    antialias: true,
    maxPixelRatio: 2,
    edges: true,
    maxFloors: 40,
  }
}
